import { lazy, useSyncExternalStore, type ComponentType, type LazyExoticComponent } from 'react';

// Dev-only views (SPEC section 10 gallery). They live outside the 18-id
// registry and the ?view= router contract: the raw ?view= value is matched
// here only when the build is a dev build. In production the map is empty, so
// no chunk is emitted and ?view=gallery falls back to the default view.

export const DEV_VIEW_IDS = ['gallery'] as const;
export type DevViewId = (typeof DEV_VIEW_IDS)[number];

const DEV_LOADERS: Readonly<Record<DevViewId, () => Promise<{ default: ComponentType }>>> = {
  gallery: () => import('./_gallery'),
};

export type DevViewMap = Readonly<Partial<Record<DevViewId, LazyExoticComponent<ComponentType>>>>;

/** Pure factory so a test can assert the production map is empty. */
export function devViewComponents(isDev: boolean): DevViewMap {
  if (!isDev) return {};
  return Object.fromEntries(DEV_VIEW_IDS.map((id) => [id, lazy(DEV_LOADERS[id])])) as DevViewMap;
}

export const DEV_VIEW_COMPONENTS: DevViewMap = devViewComponents(import.meta.env.DEV);

export function isDevViewId(value: string | null): value is DevViewId {
  return value !== null && (DEV_VIEW_IDS as readonly string[]).includes(value);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener('aleph:navigate', onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('aleph:navigate', onChange);
  };
}
function readRawView(): string | null {
  return typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('view');
}

/** The dev view id named by the URL, or null when none (or in production). */
export function useDevViewId(): DevViewId | null {
  const raw = useSyncExternalStore(subscribe, readRawView, () => null);
  if (!isDevViewId(raw)) return null;
  return raw in DEV_VIEW_COMPONENTS ? raw : null;
}
